import { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import createCachedSelector from 're-reselect';
import {
    Box,
    IconButton,
    TableBody,
    TableRow,
    Tooltip,
    Typography,
    styled,
    useTheme
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import dayjs from 'dayjs';

import ContentContainer from '@libComponents/ContentContainer';
import FilterInput from '@libComponents/FilterInput';
import TableContainer from '@libComponents/table/TableContainer';
import TableHeaderContainer from '@libComponents/table/TableHeaderContainer';
import TableCellContainer from '@libComponents/table/TableCellContainer';
import { Pagination } from '@libComponents';
import { SelectBase } from '@baseComponents/Select';
import {
    createFetchPaginatedDataset,
    datasetLoadingSelector,
    entitySelector
} from '@libs/datasets';

import {
    DOMAIN_NAME,
    ROWS_PER_PAGE,
    MODULE_FILTER_OPTIONS
} from '../common';
import { ALL_ACTIVITY_LOGS } from '../redux.datasets';
import ActionChip from './ActionChip';
import ActivityLogDetailsDialog from './ActivityLogDetailsDialog';

const StyledBox = styled(Box)(({ theme }) => ({
    width: '100%',
    overflow: 'auto',
    '& table': { tableLayout: 'fixed' },
    '& .MuiTableRow-root': {
        transition: 'background-color 0.2s ease',
        '&:hover': { backgroundColor: theme.palette.action.hover }
    }
}));

const TABLE_COLUMNS = {
    createdAt: { label: 'Timestamp', width: '16%' },
    actorEmail: { label: 'Actor', width: '20%' },
    action: { label: 'Action', width: '18%' },
    module: { label: 'Module / Target', width: '16%' },
    description: { label: 'Description', width: '22%' }
};

const defaultProps = { entity: ALL_ACTIVITY_LOGS, domain: DOMAIN_NAME };

const dataSelector = createSelector(
    (state) => entitySelector(state, DOMAIN_NAME, ALL_ACTIVITY_LOGS),
    (resp) => (resp && resp.data ? resp.data : [])
);

const totalRowsSelector = createCachedSelector(entitySelector, (resp) =>
    resp && resp.total ? parseInt(resp.total) : 0
)((state, domain, entity) => `@data-${domain}-${entity}`);

const mapState = (state, { domain, entity }) => ({
    data: dataSelector(state, domain, entity),
    loading: datasetLoadingSelector(state, domain, entity),
    totalRows: totalRowsSelector(state, domain, entity)
});

const mapDispatch = (dispatch, { entity }) => ({
    fetchDataset: ({ offset = 0, limit = ROWS_PER_PAGE, filter = {} } = {}) =>
        dispatch(
            createFetchPaginatedDataset({ entity, domain: DOMAIN_NAME })({
                limit,
                offset,
                shouldAppend: false,
                'sort-descending': true,
                filterKey: filter
            })
        )
});

/**
 * Activity Log / Audit Trail screen.
 * Displays all administrative and system events with actor, target,
 * action badge, and detailed state change diff modal.
 */
const ActivityLogAdmin = connect(
    mapState,
    mapDispatch
)(({ data = [], totalRows = 0, fetchDataset }) => {
    const theme = useTheme();
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(ROWS_PER_PAGE);
    const [moduleFilter, setModuleFilter] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedLog, setSelectedLog] = useState(null);

    const buildFilter = useCallback(
        () => ({
            ...(moduleFilter ? { module: moduleFilter } : {}),
            ...(searchTerm ? { search: searchTerm } : {})
        }),
        [moduleFilter, searchTerm]
    );

    useEffect(() => {
        setCurrentPage(1);
        fetchDataset({ offset: 0, limit: pageSize, filter: buildFilter() });
    }, [fetchDataset, buildFilter, pageSize]);

    const refetch = (page = currentPage) =>
        fetchDataset({
            offset: (page - 1) * pageSize,
            limit: pageSize,
            filter: buildFilter()
        });

    const handleChangePage = (event, newPage) => {
        setCurrentPage(newPage);
        refetch(newPage);
    };

    const handleRowsPerPageChange = (event) => {
        setPageSize(event.target.value);
        setCurrentPage(1);
    };

    return (
        <ContentContainer
            title='Activity Log & Audit Trail'
            headerBg={theme.palette.background.mainHeader}
            AdornmentComponent={
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'nowrap'
                    }}
                >
                    <FilterInput
                        name='activity-log-search'
                        placeholder='Search email / entity / action'
                        handleSearch={(value) => setSearchTerm(value)}
                    />
                    <SelectBase
                        fieldName='activity-log-module'
                        placeholder='Select Module'
                        meta={{ form: 'activity-log-module' }}
                        data={MODULE_FILTER_OPTIONS}
                        sx={{
                            width: 180,
                            mt: 1,
                            backgroundColor: theme.palette.background.white
                        }}
                        input={{
                            name: 'module',
                            value: moduleFilter,
                            onChange: (value) => setModuleFilter(value)
                        }}
                    />
                </Box>
            }
        >
            <StyledBox>
                <TableContainer>
                    <TableHeaderContainer>
                        {Object.entries(TABLE_COLUMNS).map(
                            ([key, { label, width }], index) => (
                                <TableCellContainer
                                    key={key}
                                    typography='caption'
                                    sx={{
                                        pl: index === 0 ? 4 : 2,
                                        width,
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {label}
                                </TableCellContainer>
                            )
                        )}
                        <TableCellContainer
                            typography='caption'
                            align='right'
                            sx={{ pr: 4, width: '8%', fontWeight: 'bold' }}
                        >
                            Action
                        </TableCellContainer>
                    </TableHeaderContainer>
                    <TableBody>
                        {data.map((row) => (
                            <TableRow
                                key={row.id}
                                sx={{ cursor: 'pointer' }}
                                onClick={() => setSelectedLog(row)}
                            >
                                <TableCellContainer sx={{ pl: 4 }}>
                                    <Typography variant='caption' color='text.secondary'>
                                        {dayjs(row.createdAt).format('DD MMM YYYY, hh:mm A')}
                                    </Typography>
                                </TableCellContainer>
                                <TableCellContainer sx={{ pl: 2 }}>
                                    <Typography variant='body2' noWrap title={row.actorEmail}>
                                        {row.actorEmail || 'System'}
                                    </Typography>
                                </TableCellContainer>
                                <TableCellContainer sx={{ pl: 2 }}>
                                    <ActionChip action={row.action} />
                                </TableCellContainer>
                                <TableCellContainer sx={{ pl: 2 }}>
                                    <Typography variant='body2' noWrap>
                                        <strong>{row.module}</strong> {row.entity ? `• ${row.entity}` : ''}
                                    </Typography>
                                </TableCellContainer>
                                <TableCellContainer sx={{ pl: 2 }}>
                                    <Typography
                                        variant='body2'
                                        noWrap
                                        title={row.description}
                                        sx={{ color: 'text.secondary' }}
                                    >
                                        {row.description || '—'}
                                    </Typography>
                                </TableCellContainer>
                                <TableCellContainer
                                    align='right'
                                    sx={{ pr: 4 }}
                                    onClick={(event) => event.stopPropagation()}
                                >
                                    <Tooltip title='View details' arrow placement='top'>
                                        <IconButton
                                            size='small'
                                            color='primary'
                                            onClick={() => setSelectedLog(row)}
                                        >
                                            <VisibilityIcon fontSize='small' />
                                        </IconButton>
                                    </Tooltip>
                                </TableCellContainer>
                            </TableRow>
                        ))}
                        {!data.length && (
                            <TableRow>
                                <TableCellContainer
                                    colSpan={6}
                                    align='center'
                                    sx={{ py: 6, color: 'text.secondary' }}
                                >
                                    No activity logs found for the selected filters.
                                </TableCellContainer>
                            </TableRow>
                        )}
                    </TableBody>
                </TableContainer>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination
                        count={Math.max(1, Math.ceil(totalRows / pageSize))}
                        page={currentPage}
                        onChange={handleChangePage}
                        sx={{ display: 'flex', justifyContent: 'center' }}
                        isRowsPerPageVarient
                        rowsPerPageProps={{
                            name: 'activityLogs',
                            onChange: handleRowsPerPageChange,
                            value: pageSize
                        }}
                    />
                </Box>
            </StyledBox>

            <ActivityLogDetailsDialog
                open={!!selectedLog}
                onClose={() => setSelectedLog(null)}
                log={selectedLog}
            />
        </ContentContainer>
    );
});

ActivityLogAdmin.defaultProps = defaultProps;

export default ActivityLogAdmin;
